'use client'

import {
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef
} from 'react'
import {
  getAccessToken,
  getLogoutUrl,
  AccessTokenResponse,
  refreshAccessToken
} from '../oauth'
import { makeCustomerRequest } from '@api/graphqlShopify/makeCustomerRequest'
import { Customer, CustomerApiQueryResponse } from '@components/account/types'
import customerAccountQuery from '@api/graphqlShopify/queries/customerAccountQuery'
import { transformCustomer } from '@components/account/pages/orders/helpers/transformCustomer'
import trackError from '@helpers/trackError'
import { getErrorMessage } from '@api/graphqlShopify'
import { removeCookie, setCookie } from '@helpers/session'
import { decode } from '@helpers/shopify'
import { useLogin } from './useLogin'

type MakeRequestWithRefreshCheckFn = <T, A extends unknown[]>(
  fn: (...args: [...A, token: string]) => Promise<T>, // fn takes args + token at the end
  ...args: A // Args to be passed before the token
) => Promise<T>

interface AuthContextType {
  token: string | null
  isLoading: boolean
  errorMessage: string | null
  userProfile: Customer | null
  logout: () => void
  handleCallback: (code: string, state: string) => Promise<void>
  makeRequestWithRefreshCheck: MakeRequestWithRefreshCheckFn
  refreshUserProfile: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

type Matcher = [RegExp, string]

const ERROR_TRANSLATIONS: Matcher[] = [
  [
    /Network Error/,
    'Network Error. Please check your internet connection and try again.'
  ],
  [/401/, 'You were logged out. Please log in again.'],
  [/Internal Error/, 'You can’t log in right now — please try again later.']
]
const ERROR_GENERIC = 'Account error. Please log in again.'

function translateErrorMessage(message: string): string {
  const match = ERROR_TRANSLATIONS.find(([pattern]) => pattern.test(message))
  return match ? match[1] : ERROR_GENERIC
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [userProfile, setUserProfile] = useState<Customer | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    token,
    idToken,
    refreshToken,
    tokenExpiry,
    storeTokens,
    resetCredentials
  } = useLogin()

  const id = userProfile?.id

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const refreshLockRef = useRef<Promise<string | undefined> | null>(null)

  const handleRefresh = useCallback(
    async (refreshToken: string) => {
      // If a refresh is already in progress, return existing promise to wait on
      if (refreshLockRef.current) {
        console.log(
          `refresh already in progress, user: ${id}`,
          new Date().toISOString()
        )
        return refreshLockRef.current
      }

      // Otherwise, create a new refresh promise and store it in the ref.
      refreshLockRef.current = (async () => {
        try {
          const tokenResponse = await refreshAccessToken(refreshToken)
          storeTokens(tokenResponse)
          return tokenResponse.access_token
        } catch (err) {
          const error = err as Error
          trackError(
            new Error(`Failed to refresh access token - ${error.message}`),
            {
              id,
              error,
              refreshToken,
              section: 'Account Page'
            }
          )
          resetCredentials()
        } finally {
          console.log(
            `lock complete and reset, user: ${id}`,
            new Date().toISOString()
          )
          refreshLockRef.current = null
        }
      })()

      return refreshLockRef.current
    },
    [resetCredentials, storeTokens, id]
  )

  const handleRefreshRef = useRef(handleRefresh)

  // Update the ref whenever handleRefresh changes
  useEffect(() => {
    handleRefreshRef.current = handleRefresh
  }, [handleRefresh])

  // Automatically refresh the token a minute before it expires
  useEffect(() => {
    // Clear any existing timeout when dependencies change
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (!tokenExpiry || !refreshToken) return

    const refreshTime = tokenExpiry - Date.now() - 60000 // refresh 1 minute before expiry

    if (refreshTime > 0) {
      timeoutRef.current = setTimeout(() => {
        console.log(
          `refreshing token from timer, user: ${id}`,
          new Date().toISOString()
        )
        handleRefresh(refreshToken)
      }, refreshTime)
    } else {
      // If the token is already expired or nearly so, attempt a refresh immediately
      console.log(
        `token expired - refreshing token, user: ${id}`,
        new Date().toISOString()
      )
      handleRefresh(refreshToken)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [handleRefresh, refreshToken, tokenExpiry, id])

  const makeRequestWithRefreshCheck: MakeRequestWithRefreshCheckFn =
    useCallback(
      async (fn, ...args) => {
        if (!tokenExpiry || !refreshToken || !token) {
          throw new Error(
            'Cannot makeRequestWithRefreshCheck without tokens set'
          )
        }

        const refreshTime = tokenExpiry - Date.now() - 60000 // refresh 1 minute before expiry

        if (refreshTime < 0) {
          console.log(
            `refreshing token before account request`,
            new Date().toISOString()
          )

          const updatedToken = await handleRefreshRef.current(refreshToken)

          // Use the updated token and add it as the last argument
          if (updatedToken) {
            return fn(...args, updatedToken)
          }
        }

        return fn(...args, token)
      },
      [token, refreshToken, tokenExpiry]
    )

  // Function to fetch the user profile using the token.
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    if (!token) {
      return
    }

    const maxRetries = 3
    const retryDelayMs = 500

    setIsLoading(true)

    let attempt = 0
    while (attempt < maxRetries) {
      try {
        const { data, errors } = await makeRequestWithRefreshCheck(
          makeCustomerRequest<{
            customer: CustomerApiQueryResponse
          }>,
          customerAccountQuery,
          {}
        )

        if (errors) {
          const message = errors.map(e => e.message).join(', ')
          trackError(
            new Error(
              `Error fetching user profile with error messages - ${message}`
            ),
            {
              token,
              errors: JSON.stringify(errors),
              section: 'Account Page',
              attempt
            }
          )
          attempt++
          if (attempt < maxRetries) {
            await sleep(retryDelayMs)
            continue
          } else {
            setErrorMessage(translateErrorMessage(message))
            resetCredentials()
            break
          }
        }

        const customer = transformCustomer(data.customer)
        if (customer) setUserProfile(customer)
        break // success, exit loop
      } catch (err) {
        const error = err as Error
        const errorMessage = getErrorMessage(error)
        trackError(new Error(`Error fetching user profile - ${errorMessage}`), {
          token,
          error,
          section: 'Account Page',
          attempt
        })
        attempt++
        if (attempt < maxRetries) {
          await sleep(retryDelayMs)
        } else {
          setErrorMessage(translateErrorMessage(errorMessage))
          resetCredentials()
          break
        }
      }
    }

    setIsLoading(false)
  }, [token, resetCredentials, makeRequestWithRefreshCheck])

  // Automatically fetch user profile whenever the token is updated.
  useEffect(() => {
    refreshUserProfile()
  }, [token, refreshUserProfile])

  const logout = () => {
    if (!idToken) {
      console.error(
        'Calling logout when no idToken exists',
        new Date().toISOString()
      )
    } else {
      const logoutUrl = getLogoutUrl(idToken)
      window.location.href = logoutUrl
    }

    resetCredentials()

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const handleCallback = async (code: string, state: string): Promise<void> => {
    try {
      const tokenResponse: AccessTokenResponse = await getAccessToken(
        code,
        state
      )

      storeTokens(tokenResponse)
    } catch (err) {
      const error = err as Error
      trackError(new Error(`failed to get access token - ${error.message}`), {
        error,
        id,
        section: 'Account Page',
        code,
        state
      })
      setErrorMessage(ERROR_GENERIC)
      resetCredentials()
    }
  }

  const isLoggedIn = !!id

  if (isLoggedIn) {
    // used to allow checkout events to determine if a user is logged in
    setCookie('customerId', decode(id)?.id, 60 * 60 * 24)
  } else {
    removeCookie('customerId')
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        userProfile,
        isLoading,
        logout,
        handleCallback,
        makeRequestWithRefreshCheck,
        refreshUserProfile,
        errorMessage
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
